import React, { useEffect, useState } from 'react';
import { Box, Card, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageInfosProduit from './ImageInfosProduit';
import StatistiquesProduit from './StatistiquesProduit';
import RepricerProduit from './RepricerProduit';
import AchatsLies from './AchatsLies';
import CommandesLiees from './CommandesLiees';
import api from '../utils/api';
import { getProfit, getMargin, getROI, getTVADetails } from '../utils/calculs';

const DEFAULT_MARKETPLACES = [
  { id: 'A13V1IB3VIYZZH', code: 'FR' },
  { id: 'APJ6JRA9NG5V4', code: 'IT' },
  { id: 'A1RKKUPIHCS9HS', code: 'ES' },
  { id: 'A1805IZSGTT6HS', code: 'NL' },
  { id: 'A2NODRKZP88ZB9', code: 'SE' },
  { id: 'A1C3SOZRARQ6R3', code: 'PL' },
  { id: 'AMEN7PMS3EDWL', code: 'BE' },
  { id: 'A1PA6795UKMFR9', code: 'DE' },
  { id: 'A1F83G8C2ARO7P', code: 'UK' },
];

/**
 * Composant Produit réutilisable
 * Props attendues :
 * - produit : objet produit
 * - achats : array d'achats liés à ce produit
 * - commandes : array de commandes liées à ce produit
 * - repricingRuleData, rules, exchangeRates, restrictionsByProduct, thresholds, getProfit, getMargin, getROI, getTVADetails, etc.
 * - callbacks : onEdit, onDelete, etc.
 * - MARKETPLACES, repricingTrends, fournisseurs
 */
const Produit = ({
  produit,
  achats = [],
  commandes = [],
  rules: rulesProp,
  repricingRuleData: repricingRuleDataProp,
  exchangeRates: exchangeRatesProp,
  restrictionsByProduct: restrictionsByProductProp,
  thresholds = {},
  setSelectedProduct,
  setOpenRepricerModal,
  setAsinToRefresh,
  setOpenConfirmRefresh,
  purgeRestrictionsCacheForAsin,
  setRefreshRestrictionsTrigger,
  MARKETPLACES = DEFAULT_MARKETPLACES,
  repricingTrends = {},
  fournisseurs = [],
  onEdit,
  onDelete,
}) => {
  // States locaux pour les données optionnelles
  const [rules, setRules] = useState(rulesProp || []);
  const [repricingRuleData, setRepricingRuleData] = useState(repricingRuleDataProp || {});
  const [exchangeRates, setExchangeRates] = useState(exchangeRatesProp || {});
  const [restrictionsByProduct, setRestrictionsByProduct] = useState(restrictionsByProductProp || {});
  const [achatsLocal, setAchatsLocal] = useState([]);
  const [commandesLocal, setCommandesLocal] = useState([]);

  // Règles
  useEffect(() => {
    if (rulesProp === undefined) {
      api.get('/api/rules').then(res => setRules(res.data)).catch(() => setRules([]));
    }
  }, [rulesProp]);

  // RepricingRuleData
  useEffect(() => {
    if (repricingRuleDataProp === undefined && produit?.id) {
      api.post('/api/repricing-rules/by-products', { product_ids: [produit.id] })
        .then(response => {
          const rulesArr = response.data;
          const newData = {};
          rulesArr.forEach(rule => {
            if (!newData[rule.product_id]) newData[rule.product_id] = {};
            newData[rule.product_id][rule.marketplace] = rule;
          });
          setRepricingRuleData(newData);
        });
    }
  }, [repricingRuleDataProp, produit]);

  // ExchangeRates
  useEffect(() => {
    if (exchangeRatesProp === undefined) {
      async function loadRates() {
        const rates = {};
        for (const currency of ['SEK', 'PLN', 'GBP']) {
          try {
            const rate = await api.get(`/api/currency/rate?from=${currency}&to=EUR`);
            rates[currency] = rate.data?.rate || null;
          } catch (e) {
            rates[currency] = null;
          }
        }
        setExchangeRates(rates);
      }
      loadRates();
    }
  }, [exchangeRatesProp]);

  // RestrictionsByProduct
  useEffect(() => {
    if (restrictionsByProductProp === undefined && produit?.ASIN) {
      const asin = produit.ASIN;
      const marketplaceIds = MARKETPLACES.map(mp => mp.id).join(',');
      api.get(`/api/restrictions/bulk?asins=${asin}&marketplaceIds=${marketplaceIds}`)
        .then(res => {
          setRestrictionsByProduct({ [produit.id]: res.data[asin] || {} });
        });
    }
  }, [restrictionsByProductProp, produit, MARKETPLACES]);

  // Achats liés (si non fournis)
  useEffect(() => {
    if ((!achats || achats.length === 0) && produit?.id) {
      api.post('/api/achats/by-products', { product_ids: [produit.id] })
        .then(res => setAchatsLocal(res.data?.[produit.id] || []));
    }
    // eslint-disable-next-line
  }, [produit?.id]);

  // Commandes liées (si non fournies)
  useEffect(() => {
    if ((!commandes || commandes.length === 0) && produit?.id) {
      api.post('/api/commandes/by-products', { product_ids: [produit.id] })
        .then(res => setCommandesLocal(res.data?.[produit.id] || []));
    }
    // eslint-disable-next-line
  }, [produit?.id]);

  const achatsToUse = achats && achats.length > 0 ? achats : achatsLocal;
  const commandesToUse = commandes && commandes.length > 0 ? commandes : commandesLocal;

  // Utilisation des props si fournies, sinon des states locaux
  const rulesToUse = rulesProp || rules;
  const repricingRuleDataToUse = repricingRuleDataProp || repricingRuleData;
  const exchangeRatesToUse = exchangeRatesProp || exchangeRates;
  const restrictionsByProductToUse = restrictionsByProductProp || restrictionsByProduct;

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        mb: 3,
        p: 0,
        borderRadius: 2.5,
        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
        transition: 'box-shadow 0.2s, background 0.2s',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(33,150,243,0.13)',
          background: '#f7fbff'
        },
        overflow: 'hidden',
        border: '1px solid #e3e6ef',
        position: 'relative',
        minHeight: 0
      }}
    >
      {/* Section 1 : Image + infos courtes */}
      <ImageInfosProduit produit={produit} />

      {/* Section 2 : Statistiques */}
      <StatistiquesProduit
        produit={produit}
        achatsByProduct={{ [produit.id]: achatsToUse }}
        repricingRuleData={repricingRuleDataToUse}
        thresholds={thresholds}
        getProfit={getProfit}
        getMargin={getMargin}
        getROI={getROI}
        getTVADetails={getTVADetails}
      />

      {/* Section 3 : Repricer */}
      <RepricerProduit
        produit={produit}
        repricingRuleData={repricingRuleDataToUse}
        rules={rulesToUse}
        exchangeRates={exchangeRatesToUse}
        restrictionsByProduct={restrictionsByProductToUse}
        setSelectedProduct={setSelectedProduct}
        setOpenRepricerModal={setOpenRepricerModal}
        setAsinToRefresh={setAsinToRefresh}
        setOpenConfirmRefresh={setOpenConfirmRefresh}
        purgeRestrictionsCacheForAsin={purgeRestrictionsCacheForAsin}
        setRefreshRestrictionsTrigger={setRefreshRestrictionsTrigger}
        MARKETPLACES={MARKETPLACES}
        repricingTrends={repricingTrends}
      />

      {/* Section 4 : Achats liés */}
      <Box sx={{
        flex: 1.2,
        minWidth: 180,
        bgcolor: '#fff',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        borderRight: { md: '1px solid #e3e6ef' },
        gap: 1
      }}>
        <AchatsLies achats={achatsToUse} fournisseurs={fournisseurs} />
      </Box>

      {/* Section 5 : Commandes liées */}
      <Box sx={{
        flex: 1.2,
        minWidth: 180,
        bgcolor: '#fff',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        borderRight: { md: '1px solid #e3e6ef' },
        gap: 1
      }}>
        <CommandesLiees commandes={commandesToUse.slice(0, 5)} />
      </Box>

      {/* Section Actions */}
      <Box
        sx={{
          flex: { xs: 'unset', md: '0 0 90px' },
          bgcolor: '#fff',
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          minWidth: 80,
          maxWidth: 100,
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', width: '100%' }}>
          <IconButton color="primary" onClick={() => onEdit && onEdit(produit)}><EditIcon /></IconButton>
          <IconButton color="error" onClick={() => onDelete && onDelete(produit.id)}><DeleteIcon /></IconButton>
        </Box>
      </Box>
    </Card>
  );
};

export default Produit; 